import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { MatSliderModule } from '@angular/material/slider';
import * as screenfull from 'screenfull';
// noinspection ES6PreferShortImport
import { NgClass } from '@angular/common';
import { TimePipe } from '../../pipes/time/time.pipe';
import { isMobile, isSafari } from '../../utils/mobile-utils';

const typedScreenFull = <screenfull.Screenfull>screenfull;

@Component({
  selector: 'shared-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
  imports: [NgClass, MatSliderModule, TimePipe],
})
export class VideoPlayerComponent implements AfterViewInit {
  private _cdr = inject(ChangeDetectorRef);
  @Input() videoLink: string;
  @Input() poster: string;
  @Input() controlEnabled = false;
  @Output() readonly timeUpdate$ = new EventEmitter<number>();
  @Output() readonly play$ = new EventEmitter<Event>();
  @Output() readonly pause$ = new EventEmitter<Event>();
  @Output() readonly durationChangeVideo = new EventEmitter<number>();
  volumeIcon = 'icon-learniz-volume-up';
  sliderVolumeHide = false;
  iconPlayPause = 'icon-learniz-play-arrow';
  iconVideoSize = 'icon-learniz-fullscreen';
  private _isSliding = false;

  @ViewChild('video') videoElem: ElementRef<HTMLVideoElement>;
  @ViewChild('player') player: ElementRef<HTMLDivElement>;
  @HostBinding('class.is-fullscreen') isFullscreen = false;
  isIPhone = isSafari && isMobile;
  control = false;

  @HostListener('mouseup') async slideUnpause(): Promise<void> {
    if (this._isSliding) {
      this._isSliding = false;
      await this.play();
    }
  }

  ngAfterViewInit(): void {
    if (typedScreenFull.isEnabled) {
      typedScreenFull.on('change', () => {
        this.isFullscreen = typedScreenFull.isFullscreen;
      });
    }

    this._cdr.detectChanges();
  }

  get isPaused(): boolean {
    return this.videoElem.nativeElement.paused;
  }

  get duration(): number {
    return Math.floor(this.videoElem.nativeElement.duration);
  }

  get currentTime(): number {
    return Math.floor(this.videoElem.nativeElement.currentTime);
  }

  durationChange($event): void {
    this.durationChangeVideo.emit(($event.target as HTMLVideoElement).duration);
  }

  setVolume(event: number): void {
    if (event <= 0) {
      this.volumeIcon = 'icon-learniz-volume-off';
    } else if (event <= 0.5) {
      this.volumeIcon = 'icon-learniz-volume-down';
    } else {
      this.volumeIcon = 'icon-learniz-volume-up';
    }
    this.videoElem.nativeElement.volume = event;
  }

  setVolumeOnOff(): void {
    if (this.videoElem.nativeElement.volume !== 0) {
      this.videoElem.nativeElement.volume = 0;
      this.volumeIcon = 'icon-learniz-volume-off';
    } else {
      this.videoElem.nativeElement.volume = 0.5;
      this.volumeIcon = 'icon-learniz-volume-down';
    }
  }

  setTimeSlider(time): void {
    this.pause(); // have to pause due to change value of slider when playing
    this._isSliding = true;
    this.setTime(time);
  }

  setTime(time): void {
    this.videoElem.nativeElement.currentTime = time;
  }

  async togglePlay(): Promise<void> {
    if (this.iconPlayPause === 'icon-learniz-play-arrow') {
      await this.play();
    } else {
      this.pause();
    }
  }

  async play(): Promise<void> {
    await this.videoElem.nativeElement.play();
    this.iconPlayPause = 'icon-learniz-pause';
  }

  pause(): void {
    this.videoElem.nativeElement.pause();
    this.iconPlayPause = 'icon-learniz-play-arrow';
  }

  onTimeUpdate(): void {
    if (this.timeUpdate$) {
      this.timeUpdate$.emit(this.videoElem.nativeElement.currentTime);
    }
  }

  onPause($event: Event): void {
    if (this.pause$) {
      this.pause$.emit($event);
    }
  }

  onPlay($event: Event): void {
    if (this.play$) {
      this.play$.emit($event);
    }
  }

  async toggleFullscreen(): Promise<void> {
    if (isMobile && isSafari) {
      // this is not clean but this is iphone so ...
      (this.videoElem.nativeElement as any).webkitEnterFullscreen();
      await this.play();
      return;
    }

    // will add a class to the component for safari and other browser without fullscreen
    this.isFullscreen = !this.isFullscreen;

    if (this.iconVideoSize === 'icon-learniz-fullscreen') {
      this.iconVideoSize = 'icon-learniz-minimize';
    } else {
      this.iconVideoSize = 'icon-learniz-fullscreen';
    }

    if (this.isFullscreen && typedScreenFull.isEnabled && !isSafari) {
      await typedScreenFull.request(this.player.nativeElement);
    } else if (!this.isFullscreen) {
      await typedScreenFull.exit();
      // screenFull can also be escaped by "Escap" or right click (a listener has been added to onInit: on('change'))
    }
  }

  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  showControl(): void {
    this.control = true;
  }
}
